/**
 * Skin Functional - Product Usage Guide / Routine Builder
 * Native Shopify JavaScript Controller
 * Integrates with api.skinfunctional.com backend API
 */

(function () {
  'use strict';

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeLines(value) {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) {
      return value.map(String).filter(function (s) { return s.trim(); });
    }
    return String(value)
      .split(/\n/)
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  function instructionLooksEmpty(instruction) {
    var t = String(instruction == null ? '' : instruction).trim().toLowerCase();
    return !t || t === 'n/a' || t === 'na' || t === 'none';
  }

  function dedupeProducts(products) {
    if (!Array.isArray(products)) return [];
    var seen = {};
    var out = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var sku = p && p.sku ? String(p.sku) : '';
      if (!sku || seen[sku]) continue;
      seen[sku] = true;
      out.push(p);
    }
    return out;
  }

  function sortSteps(steps) {
    if (!Array.isArray(steps)) return [];
    return steps.slice().sort(function (a, b) {
      return Number(a.step) - Number(b.step);
    });
  }

  function SfProductUsageGuide(container) {
    this.container = container;
    this.apiBaseUrl = (container.getAttribute('data-api-base-url') || 'https://api.skinfunctional.com/api').replace(/\/$/, '');
    this.showSkinTypeSelector = container.getAttribute('data-show-skin-types') === 'true';

    // State
    this.skinTypes = [];
    this.routineModes = [];
    this.products = [];
    this.selectedSkus = [];
    this.skinType = '';
    this.routineMode = container.getAttribute('data-default-routine') || 'both';
    this.searchQuery = '';
    this.isLoadingOptions = true;
    this.isSubmitting = false;
    this.result = null;

    // DOM References
    this.loaderEl = container.querySelector('[data-sf-loader]');
    this.contentEl = container.querySelector('[data-sf-content]');
    this.errorBannerEl = container.querySelector('[data-sf-options-error]');
    this.routineChipsEl = container.querySelector('[data-sf-routine-chips]');
    this.skinTypeChipsEl = container.querySelector('[data-sf-skin-type-chips]');
    this.skinTypeWrapperEl = container.querySelector('[data-sf-skin-type-wrapper]');
    this.searchInputEl = container.querySelector('[data-sf-search-input]');
    this.searchClearEl = container.querySelector('[data-sf-search-clear]');
    this.listBoxEl = container.querySelector('[data-sf-product-list]');
    this.selectionCountEl = container.querySelector('[data-sf-selection-count]');
    this.clearSelectionBtn = container.querySelector('[data-sf-clear-selection]');
    this.generateBtn = container.querySelector('[data-sf-generate-btn]');
    this.generateErrorEl = container.querySelector('[data-sf-generate-error]');
    this.resultsContainerEl = container.querySelector('[data-sf-results]');
    this.exportPdfBtn = container.querySelector('[data-sf-export-pdf]');
    this.printMetaEl = container.querySelector('[data-sf-print-meta]');

    this.bindEvents();
    this.fetchOptions();
  }

  SfProductUsageGuide.prototype.bindEvents = function () {
    var self = this;

    // Search input
    if (this.searchInputEl) {
      this.searchInputEl.addEventListener('input', function (e) {
        self.searchQuery = (e.target.value || '').trim().toLowerCase();
        self.renderProductList();
        if (self.searchClearEl) {
          self.searchClearEl.style.display = self.searchQuery ? 'flex' : 'none';
        }
      });
    }

    // Clear search
    if (this.searchClearEl) {
      this.searchClearEl.addEventListener('click', function () {
        if (self.searchInputEl) {
          self.searchInputEl.value = '';
          self.searchQuery = '';
          self.renderProductList();
          self.searchClearEl.style.display = 'none';
          self.searchInputEl.focus();
        }
      });
    }

    // Clear selection
    if (this.clearSelectionBtn) {
      this.clearSelectionBtn.addEventListener('click', function () {
        self.clearSelection();
      });
    }

    // Generate Routine
    if (this.generateBtn) {
      this.generateBtn.addEventListener('click', function () {
        self.generateRoutine();
      });
    }

    // Export PDF / Print
    if (this.exportPdfBtn) {
      this.exportPdfBtn.addEventListener('click', function () {
        self.exportAsPdf();
      });
    }

    // Retry loading options
    this.container.addEventListener('click', function (e) {
      var retryBtn = e.target.closest('[data-sf-retry-options]');
      if (retryBtn) {
        self.fetchOptions();
      }
    });
  };

  SfProductUsageGuide.prototype.fetchOptions = function () {
    var self = this;
    this.isLoadingOptions = true;
    if (this.loaderEl) this.loaderEl.style.display = 'flex';
    if (this.contentEl) this.contentEl.style.display = 'none';
    if (this.errorBannerEl) this.errorBannerEl.style.display = 'none';

    var url = this.apiBaseUrl + '/product-usage-guide-options.php';

    fetch(url, { method: 'GET', mode: 'cors' })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('Network error (' + res.status + ' ' + res.statusText + ')');
        }
        return res.json();
      })
      .then(function (data) {
        self.isLoadingOptions = false;
        if (data && data.error) {
          throw new Error(String(data.error));
        }

        self.skinTypes = Array.isArray(data.skin_types) ? data.skin_types : [];
        self.routineModes = Array.isArray(data.routine_modes) ? data.routine_modes : [];
        self.products = dedupeProducts(data.products);

        // Default skin type: first option (typically 'oily')
        if (self.skinTypes.length > 0) {
          self.skinType = self.skinTypes[0].value;
        }

        // Default routine mode
        if (self.routineModes.length > 0) {
          var hasMode = self.routineModes.some(function (rm) { return rm.value === self.routineMode; });
          if (!hasMode) {
            var both = self.routineModes.find(function (rm) { return rm.value === 'both'; });
            self.routineMode = both ? both.value : self.routineModes[0].value;
          }
        }

        if (self.loaderEl) self.loaderEl.style.display = 'none';
        if (self.contentEl) self.contentEl.style.display = 'block';

        self.renderRoutineChips();
        self.renderSkinTypeChips();
        self.renderProductList();
        self.updateActionBar();
      })
      .catch(function (err) {
        self.isLoadingOptions = false;
        if (self.loaderEl) self.loaderEl.style.display = 'none';
        if (self.errorBannerEl) {
          self.errorBannerEl.style.display = 'flex';
          var textEl = self.errorBannerEl.querySelector('[data-sf-error-text]');
          if (textEl) {
            textEl.textContent = 'Could not load options: ' + (err.message || 'Network error');
          }
        }
      });
  };

  SfProductUsageGuide.prototype.renderRoutineChips = function () {
    if (!this.routineChipsEl) return;
    var self = this;
    this.routineChipsEl.innerHTML = '';

    this.routineModes.forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sf-pub-chip' + (self.routineMode === opt.value ? ' is-active' : '');
      btn.setAttribute('aria-pressed', self.routineMode === opt.value ? 'true' : 'false');
      btn.textContent = opt.label || opt.value;
      btn.dataset.value = opt.value;

      btn.addEventListener('click', function () {
        self.routineMode = opt.value;
        var allChips = self.routineChipsEl.querySelectorAll('.sf-pub-chip');
        allChips.forEach(function (c) {
          var active = c.dataset.value === self.routineMode;
          c.classList.toggle('is-active', active);
          c.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        // If routine already generated, re-render results to reflect AM/PM visibility
        if (self.result) {
          self.renderResults();
        }
      });

      self.routineChipsEl.appendChild(btn);
    });
  };

  SfProductUsageGuide.prototype.renderSkinTypeChips = function () {
    if (!this.skinTypeChipsEl || !this.skinTypeWrapperEl) return;
    if (!this.showSkinTypeSelector) {
      this.skinTypeWrapperEl.style.display = 'none';
      return;
    }

    var self = this;
    this.skinTypeWrapperEl.style.display = 'block';
    this.skinTypeChipsEl.innerHTML = '';

    this.skinTypes.forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sf-pub-chip' + (self.skinType === opt.value ? ' is-active' : '');
      btn.setAttribute('aria-pressed', self.skinType === opt.value ? 'true' : 'false');
      btn.textContent = opt.label || opt.value;
      btn.dataset.value = opt.value;

      btn.addEventListener('click', function () {
        self.skinType = opt.value;
        var allChips = self.skinTypeChipsEl.querySelectorAll('.sf-pub-chip');
        allChips.forEach(function (c) {
          var active = c.dataset.value === self.skinType;
          c.classList.toggle('is-active', active);
          c.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });

      self.skinTypeChipsEl.appendChild(btn);
    });
  };

  SfProductUsageGuide.prototype.renderProductList = function () {
    if (!this.listBoxEl) return;
    var self = this;
    this.listBoxEl.innerHTML = '';

    var filtered = this.products;
    if (this.searchQuery) {
      filtered = this.products.filter(function (p) {
        var t = String(p.title || '').toLowerCase();
        var s = String(p.sku || '').toLowerCase();
        return t.indexOf(self.searchQuery) !== -1 || s.indexOf(self.searchQuery) !== -1;
      });
    }

    if (filtered.length === 0) {
      var empty = document.createElement('div');
      empty.className = 'sf-pub-empty-msg';
      empty.textContent = this.searchQuery
        ? 'No products match your search.'
        : 'No products are available right now.';
      this.listBoxEl.appendChild(empty);
      return;
    }

    var frag = document.createDocumentFragment();

    filtered.forEach(function (item) {
      var sku = item.sku;
      var isSelected = self.selectedSkus.indexOf(sku) !== -1;
      var isSelectable = item.selectable !== false;

      var row = document.createElement('div');
      row.className = 'sf-pub-item' +
        (isSelected ? ' is-selected' : '') +
        (!isSelectable ? ' is-disabled' : '');
      row.tabIndex = isSelectable ? 0 : -1;
      row.setAttribute('role', 'checkbox');
      row.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      row.dataset.sku = sku;

      // Image or placeholder
      var imgHtml = '';
      if (item.image_url) {
        imgHtml = '<img class="sf-pub-item__thumb" src="' + escapeHtml(item.image_url) + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="52" height="52" />';
      } else {
        imgHtml = '<div class="sf-pub-item__thumb-placeholder"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>';
      }

      // AM / PM hint
      var hints = [];
      if (item.has_am) hints.push('AM');
      if (item.has_pm) hints.push('PM');
      var hintHtml = hints.length ? '<span class="sf-pub-item__hint">' + hints.join(' • ') + '</span>' : '';

      var notSelectableHtml = !isSelectable ? '<div class="sf-pub-item__not-selectable">Not available for routines</div>' : '';

      row.innerHTML =
        imgHtml +
        '<div class="sf-pub-item__details">' +
          '<div class="sf-pub-item__title">' + escapeHtml(item.title || item.sku) + '</div>' +
          hintHtml +
          notSelectableHtml +
        '</div>' +
        '<div class="sf-pub-item__checkbox" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="none"><polyline points="20 6 9 17 4 12"/></svg>' +
        '</div>';

      if (isSelectable) {
        row.addEventListener('click', function () {
          self.toggleSku(sku);
        });

        row.addEventListener('keydown', function (e) {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            self.toggleSku(sku);
          }
        });
      }

      frag.appendChild(row);
    });

    this.listBoxEl.appendChild(frag);
  };

  SfProductUsageGuide.prototype.toggleSku = function (sku) {
    var idx = this.selectedSkus.indexOf(sku);
    if (idx !== -1) {
      this.selectedSkus.splice(idx, 1);
    } else {
      this.selectedSkus.push(sku);
    }

    // Update row state without full re-render for smooth 60fps interaction
    var row = this.listBoxEl.querySelector('[data-sku="' + sku + '"]');
    if (row) {
      var isSelected = this.selectedSkus.indexOf(sku) !== -1;
      row.classList.toggle('is-selected', isSelected);
      row.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    }

    this.updateActionBar();
  };

  SfProductUsageGuide.prototype.clearSelection = function () {
    this.selectedSkus = [];
    var selectedRows = this.listBoxEl.querySelectorAll('.sf-pub-item.is-selected');
    selectedRows.forEach(function (row) {
      row.classList.remove('is-selected');
      row.setAttribute('aria-checked', 'false');
    });
    this.updateActionBar();
  };

  SfProductUsageGuide.prototype.updateActionBar = function () {
    var count = this.selectedSkus.length;

    if (this.selectionCountEl) {
      this.selectionCountEl.textContent = count === 1 ? '1 product selected' : count + ' products selected';
    }

    if (this.clearSelectionBtn) {
      this.clearSelectionBtn.style.display = count > 0 ? 'inline-block' : 'none';
    }

    if (this.generateBtn) {
      this.generateBtn.disabled = count === 0 || this.isSubmitting || this.isLoadingOptions;
    }
  };

  SfProductUsageGuide.prototype.generateRoutine = function () {
    var self = this;
    if (this.selectedSkus.length === 0 || !this.skinType || this.isSubmitting) return;

    this.isSubmitting = true;
    if (this.generateBtn) {
      this.generateBtn.disabled = true;
      this.generateBtn.setAttribute('aria-busy', 'true');
      var btnText = this.generateBtn.querySelector('[data-sf-btn-text]');
      var spinner = this.generateBtn.querySelector('[data-sf-btn-spinner]');
      if (btnText) btnText.textContent = 'Generating routine...';
      if (spinner) spinner.style.display = 'inline-block';
    }

    if (this.generateErrorEl) {
      this.generateErrorEl.style.display = 'none';
    }

    var payload = {
      skus: this.selectedSkus,
      skin_type: this.skinType,
      routine: this.routineMode
    };

    var url = this.apiBaseUrl + '/generate-routine.php';

    fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('Server returned ' + res.status + ' ' + res.statusText);
        }
        return res.json();
      })
      .then(function (data) {
        self.isSubmitting = false;
        self.resetGenerateBtnState();

        if (data && data.error) {
          throw new Error(String(data.error));
        }

        self.result = data;
        self.renderResults();

        // Smooth scroll to results
        if (self.resultsContainerEl) {
          self.resultsContainerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      })
      .catch(function (err) {
        self.isSubmitting = false;
        self.resetGenerateBtnState();

        if (self.generateErrorEl) {
          self.generateErrorEl.style.display = 'flex';
          var textEl = self.generateErrorEl.querySelector('[data-sf-error-text]');
          if (textEl) {
            textEl.textContent = err.message || 'Could not generate routine. Please check your connection and try again.';
          }
        }
      });
  };

  SfProductUsageGuide.prototype.resetGenerateBtnState = function () {
    if (this.generateBtn) {
      this.generateBtn.disabled = this.selectedSkus.length === 0;
      this.generateBtn.removeAttribute('aria-busy');
      var btnText = this.generateBtn.querySelector('[data-sf-btn-text]');
      var spinner = this.generateBtn.querySelector('[data-sf-btn-spinner]');
      if (btnText) btnText.textContent = 'Generate routine';
      if (spinner) spinner.style.display = 'none';
    }
  };

  SfProductUsageGuide.prototype.renderResults = function () {
    if (!this.resultsContainerEl || !this.result) return;

    var result = this.result;
    var conflicts = Array.isArray(result.conflicts) ? result.conflicts : [];
    var showAm = this.routineMode === 'am' || this.routineMode === 'both';
    var showPm = this.routineMode === 'pm' || this.routineMode === 'both';
    var groupedWarnings = Array.isArray(result.grouped_warnings) ? result.grouped_warnings : [];
    var groupedIngredients = Array.isArray(result.grouped_ingredients) ? result.grouped_ingredients : [];

    var html = '';

    // Conflicts Banner
    if (conflicts.length > 0) {
      html += '<div class="sf-pub-conflicts" role="alert">' +
        '<div class="sf-pub-conflicts__title">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>' +
          '<span>Conflicts & Incompatibilities</span>' +
        '</div>' +
        '<ul class="sf-pub-conflicts__list">';
      conflicts.forEach(function (c) {
        html += '<li class="sf-pub-conflicts__item">• ' + escapeHtml(c) + '</li>';
      });
      html += '</ul></div>';
    }

    // AM Section
    if (showAm) {
      html += this.renderRoutineSection('Morning (AM)', result.routine && result.routine.am, 'sun');
    }

    // PM Section
    if (showPm) {
      html += this.renderRoutineSection('Evening (PM)', result.routine && result.routine.pm, 'moon');
    }

    // Accordions for Summaries
    if (groupedWarnings.length > 0 || groupedIngredients.length > 0) {
      html += '<div class="sf-pub-accordion-group">';

      if (groupedWarnings.length > 0) {
        html += '<details class="sf-pub-details">' +
          '<summary class="sf-pub-details__summary">' +
            '<span>All Warnings (Summary)</span>' +
            '<svg class="sf-pub-details__summary-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>' +
          '</summary>' +
          '<div class="sf-pub-details__content">';
        groupedWarnings.forEach(function (block) {
          html += '<div class="sf-pub-details__block">' + escapeHtml(block) + '</div>';
        });
        html += '</div></details>';
      }

      if (groupedIngredients.length > 0) {
        html += '<details class="sf-pub-details">' +
          '<summary class="sf-pub-details__summary">' +
            '<span>All Active Ingredients (Summary)</span>' +
            '<svg class="sf-pub-details__summary-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>' +
          '</summary>' +
          '<div class="sf-pub-details__content">';
        groupedIngredients.forEach(function (block) {
          html += '<div class="sf-pub-details__block">' + escapeHtml(block) + '</div>';
        });
        html += '</div></details>';
      }

      html += '</div>';
    }

    this.resultsContainerEl.innerHTML = html;
    this.resultsContainerEl.style.display = 'block';

    if (this.exportPdfBtn) {
      this.exportPdfBtn.style.display = 'inline-flex';
    }
  };

  SfProductUsageGuide.prototype.renderRoutineSection = function (title, steps, iconType) {
    var ordered = sortSteps(steps);
    var meaningful = ordered.filter(function (s) {
      return !instructionLooksEmpty(s.instruction);
    });

    var iconSvg = iconType === 'sun'
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';

    var out = '<div class="sf-pub-routine-section">' +
      '<h3 class="sf-pub-routine-title">' + iconSvg + '<span>' + escapeHtml(title) + '</span></h3>';

    if (meaningful.length === 0) {
      out += '<p class="sf-pub-routine-empty">No steps for this part of the day.</p></div>';
      return out;
    }

    meaningful.forEach(function (step) {
      var imgUri = step.image || step.image_url;
      var ingredients = normalizeLines(step.active_ingredients);
      var warnings = normalizeLines(step.warnings);

      out += '<div class="sf-pub-step">' +
        '<div class="sf-pub-step__head">' +
          '<span class="sf-pub-step__badge">Step ' + escapeHtml(step.step) + '</span>' +
          '<h4 class="sf-pub-step__title">' + escapeHtml(step.title || 'Product') + '</h4>' +
        '</div>';

      if (imgUri) {
        out += '<div class="sf-pub-step__image-wrap">' +
          '<img class="sf-pub-step__image" src="' + escapeHtml(imgUri) + '" alt="' + escapeHtml(step.title || 'Product') + '" loading="lazy" />' +
        '</div>';
      }

      if (!instructionLooksEmpty(step.instruction)) {
        out += '<p class="sf-pub-step__instruction">' + escapeHtml(step.instruction) + '</p>';
      }

      if (ingredients.length > 0) {
        out += '<div class="sf-pub-step__meta">' +
          '<span class="sf-pub-step__meta-label">Active ingredients</span>' +
          '<ul class="sf-pub-step__meta-list">';
        ingredients.forEach(function (line) {
          out += '<li class="sf-pub-step__meta-item">' + escapeHtml(line) + '</li>';
        });
        out += '</ul></div>';
      }

      if (warnings.length > 0) {
        out += '<div class="sf-pub-step__meta">' +
          '<span class="sf-pub-step__meta-label sf-pub-step__meta-label--warn">Warnings</span>' +
          '<ul class="sf-pub-step__meta-list sf-pub-step__meta-list--warn">';
        warnings.forEach(function (line) {
          out += '<li class="sf-pub-step__meta-item">' + escapeHtml(line) + '</li>';
        });
        out += '</ul></div>';
      }

      out += '</div>';
    });

    out += '</div>';
    return out;
  };

  SfProductUsageGuide.prototype.exportAsPdf = function () {
    if (!this.result) return;

    var dateStr = new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    var skinTypeLabel = this.skinType;
    var currentSt = this.skinTypes.find(function (x) { return x.value === this.skinType; }, this);
    if (currentSt) skinTypeLabel = currentSt.label;

    var routineModeLabel = this.routineMode;
    var currentRm = this.routineModes.find(function (x) { return x.value === this.routineMode; }, this);
    if (currentRm) routineModeLabel = currentRm.label;

    if (this.printMetaEl) {
      this.printMetaEl.innerHTML =
        '<div><strong>Generated:</strong> ' + escapeHtml(dateStr) + '</div>' +
        '<div><strong>Skin Type:</strong> ' + escapeHtml(skinTypeLabel) + ' | <strong>Routine:</strong> ' + escapeHtml(routineModeLabel) + '</div>';
    }

    // Open any summary accordions so they appear in the PDF
    if (this.resultsContainerEl) {
      var accordions = this.resultsContainerEl.querySelectorAll('.sf-pub-details');
      accordions.forEach(function (d) { d.open = true; });
    }

    this.downloadPdf(dateStr, skinTypeLabel, routineModeLabel);
  };

  var HTML2PDF_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
  var html2pdfPromise = null;

  function loadHtml2Pdf() {
    if (window.html2pdf) return Promise.resolve(window.html2pdf);
    if (html2pdfPromise) return html2pdfPromise;
    html2pdfPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = HTML2PDF_SRC;
      script.async = true;
      script.onload = function () {
        window.html2pdf ? resolve(window.html2pdf) : reject(new Error('html2pdf unavailable'));
      };
      script.onerror = function () {
        html2pdfPromise = null;
        reject(new Error('Failed to load html2pdf'));
      };
      document.head.appendChild(script);
    });
    return html2pdfPromise;
  }

  // Builds a clean copy of the routine and downloads it as a PDF file
  SfProductUsageGuide.prototype.downloadPdf = function (dateStr, skinTypeLabel, routineModeLabel) {
    var self = this;
    var btn = this.exportPdfBtn;
    if (!this.resultsContainerEl || (btn && btn.disabled)) return;

    var originalLabel = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Preparing PDF…';
    }

    var doc = document.createElement('div');
    doc.className = 'sf-pub-pdf';
    doc.innerHTML =
      '<div class="sf-pub-pdf__header">' +
        '<div class="sf-pub-pdf__title">My SKIN functional Routine</div>' +
        '<div class="sf-pub-pdf__meta">' +
          '<div><strong>Generated:</strong> ' + escapeHtml(dateStr) + '</div>' +
          '<div><strong>Skin Type:</strong> ' + escapeHtml(skinTypeLabel) + ' | <strong>Routine:</strong> ' + escapeHtml(routineModeLabel) + '</div>' +
        '</div>' +
      '</div>';

    var results = this.resultsContainerEl.cloneNode(true);
    results.removeAttribute('data-sf-results');
    results.style.display = 'block';
    results.querySelectorAll('img').forEach(function (img) {
      img.removeAttribute('loading');
      img.setAttribute('crossorigin', 'anonymous');
    });
    doc.appendChild(results);

    var d = new Date();
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    var filename = 'skin-functional-routine-' + d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + '.pdf';

    function restore() {
      if (btn) {
        btn.disabled = false;
        btn.textContent = originalLabel;
      }
    }

    loadHtml2Pdf()
      .then(function (html2pdf) {
        return html2pdf()
          .set({
            margin: [12, 12, 14, 12],
            filename: filename,
            image: { type: 'jpeg', quality: 0.95 },
            html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', scrollX: 0, scrollY: 0 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'], avoid: ['.sf-pub-step', '.sf-pub-conflicts', '.sf-pub-details', '.sf-pub-routine-title'] }
          })
          .from(doc)
          .save();
      })
      .then(restore)
      .catch(function (err) {
        console.error('[SF Usage Guide] PDF export failed', err);
        restore();
        if (btn) {
          btn.textContent = 'Download failed – try again';
          setTimeout(function () { btn.textContent = originalLabel; }, 3000);
        }
      });
  };

  // Lifecycle & Initialization
  function initProductUsageGuideSections() {
    var sections = document.querySelectorAll('[data-sf-product-usage-guide]');
    sections.forEach(function (container) {
      if (!container._sfPubInstance) {
        container._sfPubInstance = new SfProductUsageGuide(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductUsageGuideSections);
  } else {
    initProductUsageGuideSections();
  }

  // Shopify Theme Editor Integration
  document.addEventListener('shopify:section:load', function (e) {
    var container = e.target.querySelector('[data-sf-product-usage-guide]');
    if (container && !container._sfPubInstance) {
      container._sfPubInstance = new SfProductUsageGuide(container);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var container = e.target.querySelector('[data-sf-product-usage-guide]');
    if (container && container._sfPubInstance) {
      container._sfPubInstance = null;
    }
  });

})();
